import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  useTheme,
  useMediaQuery,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  keyframes,
  alpha
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Person as RenterIcon,
  Home as OwnerIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountBalance as FinanceIcon,
  SwapHoriz as SwapIcon,
  TrendingUp as EarningsIcon,
  AccountBalanceWallet as WalletIcon,
  AccountBalance,
  Add as AddIcon,
  Payments as PaymentsIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RESERVATION_STATUSES } from '../../constants/paymentConstants';
import { toast } from 'react-toastify';

// Services
import { paymentService } from '../../services/paymentService';

// Components
import PaymentHistoryInfinite from './PaymentHistoryInfinite';
import DepositsHistoryInfinite from './DepositsHistoryInfinite';

// Constants
import { 
  PAYMENT_TYPES, 
  PAYMENT_STATUSES, 
  PAYMENT_METHODS, 
  CURRENCIES,
  STRIPE_METHODS,
  CASH_METHODS,
  SUCCESS_STATUSES 
} from '../../constants/paymentConstants';

// Gradient moderne
const gradientStyle = {
  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
  color: 'white'
};

const RenterFinancialCenter = () => {
  const { t } = useTranslation(['financial', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // États du composant
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [expandedCurrency, setExpandedCurrency] = useState({
    cash: 1,     // Nombre de devises à afficher pour les paiements cash
    online: 1,   // Nombre de devises à afficher pour les paiements en ligne
    general: 1   // Nombre de devises à afficher dans la section principale
  });
  const [statistics, setStatistics] = useState({
    totalPaid: 0,
    totalReservations: 0,
    pendingPayments: 0,
    completedPayments: 0
  });
  const [error, setError] = useState(null);
  const [highlightHistory, setHighlightHistory] = useState(false);
  const [highlightDeposits, setHighlightDeposits] = useState(false);
  
  // 📊 Fonction simple pour filtrer les données et calculer les breakdowns corrects
  const getFilteredBreakdowns = () => {
    if (!payments || !deposits) return { cashFiltered: {}, onlineFiltered: {}, depositsFiltered: {} };
    
    console.log('🔍 getFilteredBreakdowns called with:', payments.length, 'payments');
    
    // Debug - Analyser TOUS les paiements par devise et méthode
    const allPaymentsByMethod = {};
    payments.forEach(p => {
      const currency = p.currency || 'EUR';
      const method = (p.method === 'cash' || p.payment_method === 'cash') ? 'cash' : 'online';
      const key = `${currency}-${method}`;
      
      if (!allPaymentsByMethod[key]) {
        allPaymentsByMethod[key] = 0;
      }
      allPaymentsByMethod[key]++;
    });
    console.log('📊 ALL payments by currency-method:', allPaymentsByMethod);
    
    // Statistiques des réservations par statut
    const reservationsStats = {
      total: reservations.length,
      byStatus: {}
    };
    
    console.log('🔍 RESERVATIONS debug - First reservation:', reservations[0]);
    console.log('🔍 RESERVATIONS debug - Available fields:', reservations[0] ? Object.keys(reservations[0]) : 'No reservations');
    console.log('🔍 RESERVATION_STATUSES constants:', RESERVATION_STATUSES);
    
    reservations.forEach(r => {
      // Utiliser la même logique que PaymentHistoryInfinite avec fallback
      let status = r.reservation_status || 'pending';
      
      // Vérifier que le statut existe dans les constantes, sinon utiliser 'pending'
      if (!Object.values(RESERVATION_STATUSES).includes(status)) {
        status = RESERVATION_STATUSES.PENDING || 'pending';
      }
      
      console.log('🔍 RESERVATION status found:', { 
        id: r.id, 
        reservation_status: r.reservation_status, 
        final_status: status 
      });
      
      if (!reservationsStats.byStatus[status]) {
        reservationsStats.byStatus[status] = 0;
      }
      reservationsStats.byStatus[status]++;
    });
    
    console.log('🔍 RESERVATIONS final stats:', reservationsStats);
    
    // Statistiques des paiements/transactions par statut
    const paymentsStats = {
      total: payments.length,
      byStatus: {}
    };
    
    payments.forEach(p => {
      const status = p.status || 'unknown';
      if (!paymentsStats.byStatus[status]) {
        paymentsStats.byStatus[status] = 0;
      }
      paymentsStats.byStatus[status]++;
    });
    
    // Filtrer les paiements selon les nouvelles règles de gestion
    const filteredPayments = payments.filter(p => {
      // Exclure toujours les dépôts
      if (p.type === 'deposit' || p.type === 'deposit_refund') {
        return false;
      }
      
      // Inclure seulement les types valides
      if (!['rental', 'commission', 'tax'].includes(p.type)) {
        return false;
      }
      
      // Détecter le type de paiement
      const isStripePayment = p.method !== 'cash' && p.payment_method !== 'cash';
      const isCashPayment = p.method === 'cash' || p.payment_method === 'cash';
      
      let isValid = false;
      
      if (isStripePayment) {
        // Règle 1: Si Stripe - exclure seulement 'cancelled', inclure tous les autres statuts de réservation
        isValid = p.reservation_status !== 'cancelled';
      } else if (isCashPayment) {
        // Règle 2: Si Cash - exclure 'cancelled' ET 'confirmed', inclure le reste
        isValid = p.reservation_status !== 'cancelled' && p.reservation_status !== 'confirmed';
      }
      
      // Debug pour les exclusions
      if (!isValid) {
        console.log('❌ Payment EXCLUDED:', {
          currency: p.currency,
          amount: p.amount,
          type: p.type,
          status: p.status,
          reservation_status: p.reservation_status,
          method: p.method || p.payment_method,
          paymentType: isStripePayment ? 'Stripe' : 'Cash',
          reason: p.reservation_status === 'cancelled' ? 'cancelled reservation' : 
                  (isCashPayment && p.reservation_status === 'confirmed') ? 'cash + confirmed reservation' : 
                  'other'
        });
      }
      
      return isValid;
    });
    
    console.log('🔍 Filtered payments:', filteredPayments.length, 'from', payments.length);
    
    // Debug - Compter par méthode de paiement
    const cashCount = filteredPayments.filter(p => p.method === 'cash' || p.payment_method === 'cash').length;
    const onlineCount = filteredPayments.filter(p => p.method !== 'cash' && p.payment_method !== 'cash').length;
    console.log('🔍 Payment methods - Cash:', cashCount, 'Online:', onlineCount);
    
    // Filtrer les deposits - séparer par statut pour affichage détaillé
    console.log('🔍 DEPOSITS - Total avant filtrage:', deposits.length);
    
    // Créer des statistiques détaillées par statut
    const depositsStats = {
      total: deposits.length,
      byStatus: {},
      cancelled: deposits.filter(d => d.reservation_status === 'cancelled').length,
      nonCancelled: deposits.filter(d => d.reservation_status !== 'cancelled').length
    };
    
    // Compter par statut (seulement les non-annulés)
    deposits.filter(d => d.reservation_status !== 'cancelled').forEach(d => {
      const status = d.status || 'unknown';
      if (!depositsStats.byStatus[status]) {
        depositsStats.byStatus[status] = 0;
      }
      depositsStats.byStatus[status]++;
    });
    
    console.log('🔍 DEPOSITS stats:', depositsStats);
    
    // Pour la compatibilité, garder les deposits filtrés (held/completed seulement)
    const filteredDeposits = deposits.filter(d => 
      d.reservation_status !== 'cancelled' && 
      (d.status === 'held' || d.status === 'completed')
    );
    console.log('🔍 DEPOSITS - Après filtrage (non annulés, held/completed):', filteredDeposits.length);
    
    const cashFiltered = { byCurrency: {} };
    const onlineFiltered = { byCurrency: {} };
    const depositsFiltered = {};
    
    // Traiter les paiements filtrés
    filteredPayments.forEach(payment => {
      const currency = payment.currency || 'EUR';
      const amount = parseFloat(payment.amount) || 0;
      const method = (payment.method === 'cash' || payment.payment_method === 'cash') ? 'cash' : 'online'; // Vérifier les 2 champs
      const paymentType = payment.type || 'rental';
      
      // Debug détaillé pour EUR
      if (currency === 'EUR') {
        console.log('💶 EUR Payment INCLUDED:', {
          id: payment.id,
          currency,
          amount,
          type: paymentType,
          method: method,
          reservation_status: payment.reservation_status,
          payment_status: payment.status,
          created_at: payment.created_at || payment.date,
          paymentMethod: payment.method || payment.payment_method
        });
      }
      
      // Debug pour cash
      if (method === 'cash') {
        console.log('💰 CASH Payment found:', {
          currency,
          amount,
          type: paymentType,
          method: payment.method,
          payment_method: payment.payment_method
        });
      }
      
      const target = method === 'cash' ? cashFiltered : onlineFiltered;
      
      if (!target.byCurrency[currency]) {
        target.byCurrency[currency] = {
          amount: 0,
          count: 0,
          breakdown: { rental: 0, commission: 0, tax: 0 }
        };
      }
      
      target.byCurrency[currency].amount += amount;
      target.byCurrency[currency].count += 1;
      target.byCurrency[currency].breakdown[paymentType] += amount;
    });
    
    // Traiter les deposits filtrés
    filteredDeposits.forEach(deposit => {
      const currency = deposit.currency || 'EUR';
      const amount = parseFloat(deposit.amount) || 0;
      
      if (!depositsFiltered[currency]) {
        depositsFiltered[currency] = { totalAmount: 0, count: 0 };
      }
      
      depositsFiltered[currency].totalAmount += amount;
      depositsFiltered[currency].count += 1;
    });
    
    // Créer currencyBreakdownFiltered avec la même logique que Cash/Online
    const currencyBreakdownFiltered = {};
    filteredPayments.forEach(payment => {
      const currency = payment.currency || 'EUR';
      const amount = parseFloat(payment.amount) || 0;
      const method = (payment.method === 'cash' || payment.payment_method === 'cash') ? 'cash' : 'online';
      const paymentType = payment.type || 'rental';
      
      if (!currencyBreakdownFiltered[currency]) {
        currencyBreakdownFiltered[currency] = {
          totalAmount: 0,
          transactions: 0,
          avgTransaction: 0,
          breakdown: { rental: 0, commission: 0, tax: 0 },
          byMethod: { cash: 0, online: 0 }
        };
      }
      
      currencyBreakdownFiltered[currency].totalAmount += amount;
      currencyBreakdownFiltered[currency].transactions += 1;
      currencyBreakdownFiltered[currency].breakdown[paymentType] += amount;
      currencyBreakdownFiltered[currency].byMethod[method] += amount;
    });
    
    // Calculer les moyennes
    Object.keys(currencyBreakdownFiltered).forEach(currency => {
      const data = currencyBreakdownFiltered[currency];
      data.avgTransaction = data.transactions > 0 ? data.totalAmount / data.transactions : 0;
    });
    
    console.log('🔍 FINAL RESULTS:');
    console.log('💰 Cash by currency:', cashFiltered.byCurrency);
    console.log('💳 Online by currency:', onlineFiltered.byCurrency);
    console.log('💱 Currency breakdown filtered:', currencyBreakdownFiltered);
    
    // Debug spécifique pour EUR
    if (currencyBreakdownFiltered.EUR) {
      console.log('💶 TOTAL EUR CALCULÉ:', {
        totalAmount: currencyBreakdownFiltered.EUR.totalAmount,
        transactions: currencyBreakdownFiltered.EUR.transactions,
        breakdown: currencyBreakdownFiltered.EUR.breakdown,
        byMethod: currencyBreakdownFiltered.EUR.byMethod
      });
    }
    
    return { 
      cashFiltered, 
      onlineFiltered, 
      depositsFiltered, 
      currencyBreakdownFiltered,
      depositsStats, // Stats détaillées des dépôts
      reservationsStats, // Stats détaillées des réservations
      paymentsStats // Stats détaillées des paiements
    };
  };
  
  // États du sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Animation de pulsation pour le FAB
  const pulseAnimation = keyframes`
    0% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
    50% {
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 12px 25px rgba(98, 0, 238, 0.5);
    }
    100% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
  `;

  // Gestion du sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Navigation vers la vue propriétaire
  const handleOwnerViewNavigation = () => {
    setSidebarOpen(false);
    navigate('/financial-owner');
  };

  // Configuration des éléments du sidebar
  const sidebarItems = [
    {
      title: t('financial:sidebar.dashboard', 'Tableau de bord'),
      icon: <DashboardIcon />,
      action: () => {
        setSidebarOpen(false);
        navigate('/dashboard');
      },
      description: t('financial:sidebar.dashboardDesc', 'Retour au tableau de bord principal')
    },
    { type: 'divider' },
    {
      title: t('financial:views.renter.title', 'Vue Locataire'),
      icon: <RenterIcon />,
      action: () => setSidebarOpen(false),
      active: true,
      description: t('financial:views.renter.shortDesc', 'Mes paiements')
    },
    {
      title: t('financial:views.owner.title', 'Vue Propriétaire'),
      icon: <OwnerIcon />,
      action: handleOwnerViewNavigation,
      active: false,
      description: t('financial:views.owner.shortDesc', 'Mes revenus')
    },
    { type: 'divider' },
    {
      title: t('financial:sidebar.wallet', 'Portefeuille'),
      icon: <WalletIcon />,
      action: () => {
        setSidebarOpen(false);
        console.log('Navigation vers portefeuille');
      },
      description: t('financial:sidebar.walletDesc', 'Solde et moyens de paiement')
    }
  ];

  // Chargement des données au montage du composant
  useEffect(() => {
    console.log('🔄 RenterFinancialCenter mounted, loading data...');
    loadRenterFinancialData();
  }, []);

  const loadRenterFinancialData = async () => {
    console.log('🔄 Starting loadRenterFinancialData...');
    try {
      setLoading(true);
      setError(null);

      console.log('� Loading renter dashboard data...');

      // Utilisation de la nouvelle méthode getRenterDashboard
      const dashboardResponse = await paymentService.getRenterDashboard();
      console.log('🔍 Dashboard response:', dashboardResponse);
      
      if (!dashboardResponse.success) {
        throw new Error(dashboardResponse.error || 'Failed to load dashboard data');
      }

      const dashboardData = dashboardResponse.data;
      
      // Extraction des données détaillées du dashboard selon notre nouvelle structure
      const paymentsData = dashboardData.payments || [];
      const depositsData = dashboardData.deposits || [];
      const reservationsWithPayments = dashboardData.reservationsWithPayments || [];
      const reservationsData = dashboardData.reservations || reservationsWithPayments; // Essayer d'abord reservations pures
      const stats = dashboardData.stats || {};
      const currencyBreakdown = dashboardData.currencyBreakdown || {};
      const paymentMethodBreakdown = dashboardData.paymentMethodBreakdown || {};
      const depositsBreakdown = dashboardData.depositsBreakdown || {};
      const transactionStats = dashboardData.transactionStats || {};
      
      // Utilisation des nouvelles statistiques calculées uniquement à partir de l'historique des paiements
      const detailedStats = {
        // Totaux généraux basés sur les paiements complétés uniquement
        totalPaid: stats.totalPaid || 0,
        totalPending: stats.totalPending || 0,
        
        // Répartition par type
        totalRental: stats.rentalPaid || 0,
        totalCommissions: stats.commissionPaid || 0,
        totalTaxes: stats.taxesPaid || 0,
        
        // Répartition par méthode
        cashTotal: stats.cashTotal || 0,
        onlineTotal: stats.onlineTotal || 0,
        
        // Réservations - la seule source de vérité
        uniqueReservationsCount: stats.uniqueReservationsCount || 0,
        reservationsWithCompletedPayments: stats.reservationsWithCompleted || 0,
        reservationsWithPendingPayments: stats.reservationsWithPending || 0,
        
        // Paiements
        totalTransactions: stats.totalPayments || 0,
        completedPayments: stats.completedPayments || 0,
        pendingPayments: stats.pendingPayments || 0,
        
        // Répartition détaillée
        transactionStats: transactionStats,
        currencyBreakdown: currencyBreakdown,
        paymentMethodBreakdown: paymentMethodBreakdown,
        depositsBreakdown: depositsBreakdown,
        
        // Dates importantes
        firstPaymentDate: dashboardData.firstPaymentDate,
        lastPaymentDate: dashboardData.lastPaymentDate,
        
        // Données structurées par réservation
        reservationsWithPayments: reservationsWithPayments
      };

      setPayments(paymentsData);
      setDeposits(depositsData);
      setReservations(reservationsData); // Utiliser les vraies réservations
      setStatistics(detailedStats);

      console.log('✅ Renter dashboard loaded successfully:', {
        payments: paymentsData.length,
        reservations: reservationsWithPayments.length,
        currencyBreakdown: Object.keys(currencyBreakdown).length,
        stats: detailedStats
      });

    } catch (error) {
      console.error('❌ Erreur lors du chargement du dashboard locataire:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setError(error.message || 'Erreur lors du chargement des données financières');
      
      toast.error(
        t('financial:errors.loadFailed', 'Erreur lors du chargement des données financières'),
        { autoClose: 8000 }
      );
    } finally {
      console.log('🔄 Setting loading to false...');
      setLoading(false);
      console.log('✅ Loading state updated');
    }
  };

  // Formatage des montants avec support multi-devises
  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Fonction pour montrer plus de devises
  const handleShowMoreCurrencies = (category) => {
    // Récupère le nombre total de devises disponibles pour cette catégorie
    let totalCurrencies = 0;
    if (category === 'general') {
      totalCurrencies = Object.entries(statistics.currencyBreakdown || {})
        .filter(([_, data]) => data.transactions > 0).length;
    } else if (category === 'cash') {
      totalCurrencies = Object.keys(statistics.paymentMethodBreakdown?.cash?.byCurrency || {}).length;
    } else if (category === 'online') {
      totalCurrencies = Object.keys(statistics.paymentMethodBreakdown?.online?.byCurrency || {}).length;
    }
    
    // Affiche toutes les devises restantes
    setExpandedCurrency(prev => ({
      ...prev,
      [category]: totalCurrencies
    }));
  };

  // Groupage des paiements par devise
  const groupPaymentsByCurrency = (payments) => {
    return paymentService.groupByCurrency(payments);
  };

  // Groupage des paiements par méthode (Stripe vs Cash)
  const groupPaymentsByMethod = (payments) => {
    return paymentService.groupByMethod(payments);
  };

  // Formatage des dates
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Obtention du libellé de la méthode de paiement (utilise les constantes)
  const getPaymentMethodLabel = (method) => {
    const methodLabels = {
      [PAYMENT_METHODS.STRIPE]: 'Stripe',
      [PAYMENT_METHODS.CASH]: t('financial:methods.cash', 'Espèces'),
      [PAYMENT_METHODS.CARD]: t('financial:methods.card', 'Carte'),
      [PAYMENT_METHODS.VIREMENT]: t('financial:methods.wire', 'Virement'),
      [PAYMENT_METHODS.BANK_TRANSFER]: t('financial:methods.bank', 'Virement bancaire'),
      [PAYMENT_METHODS.ADMIN_ADJUSTMENT]: t('financial:methods.admin', 'Ajustement admin')
    };
    return methodLabels[method] || method;
  };

  // Distinction entre méthodes Stripe et Cash
  const isStripeMethod = (method) => {
    return STRIPE_METHODS.includes(method);
  };

  const isCashMethod = (method) => {
    return CASH_METHODS.includes(method);
  };



  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          {t('financial:loading', 'Chargement des données financières...')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('financial:errors.title', 'Erreur de chargement')}
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={loadRenterFinancialData}
            color="primary"
          >
            {t('common:retry', 'Réessayer')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Contenu du sidebar
  const SidebarContent = () => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header du sidebar avec gradient */}
      <Box
        sx={{
          ...gradientStyle,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={toggleSidebar}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Avatar
          src={user?.avatar}
          sx={{ width: 48, height: 48, border: '2px solid white' }}
        >
          <FinanceIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {t('financial:renter.title', 'Finance Locataire')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
      </Box>

      {/* Menu du sidebar */}
      <Box sx={{ flex: 1, p: 2 }}>
        {sidebarItems.map((item, index) => {
          if (item.type === 'divider') {
            return <Divider key={index} sx={{ my: 1 }} />;
          }

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                mb: 1,
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                backgroundColor: item.active 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                borderLeft: item.active 
                  ? `4px solid ${theme.palette.primary.main}`
                  : '4px solid transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateX(4px)',
                  boxShadow: theme.shadows[2]
                }
              }}
              onClick={item.action}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    color: item.active ? theme.palette.primary.main : theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: item.active ? 600 : 400,
                      color: item.active ? theme.palette.primary.main : theme.palette.text.primary
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Footer du sidebar */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SwapIcon />}
          onClick={() => {
            setSidebarOpen(false);
            navigate('/dashboard');
          }}
          sx={{ borderRadius: 2 }}
        >
          {t('financial:sidebar.backToDashboard', 'Retour au Hub')}
        </Button>
      </Box>
    </Box>
  );

  // Contenu principal avec navigation
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button onClick={loadRenterFinancialData} variant="contained">
          {t('common:retry', 'Réessayer')}
        </Button>
      </Box>
    );
  }

  // Rendu principal du composant
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{ 
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            p: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease'
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              textAlign: 'left',
              mb: 1
            }}
          >
            {t('financial:renter.title', 'Mon Centre Financier')}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'left',
              fontSize: '1.1rem',
              opacity: 0.9
            }}
          >
            {t('financial:renter.subtitle', 'Gérez vos paiements et historique de location')}
          </Typography>
        </Box>
      </Box>

      {/* Cartes de statistiques détaillées */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Réservations uniques - source unique de vérité */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              height: '180px', // Hauteur fixe
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[8],
                background: `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                '& .card-icon': {
                  color: theme.palette.primary.main,
                  transform: 'scale(1.1)'
                },
                '& .card-title': {
                  color: theme.palette.primary.main
                },
                '& .card-value': {
                  color: theme.palette.primary.dark
                }
              }
            }}
            onClick={() => {
              const historySection = document.getElementById('payment-history-section');
              if (historySection) {
                historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHighlightHistory(true);
                setTimeout(() => setHighlightHistory(false), 2000);
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DashboardIcon 
                  className="card-icon" 
                  color="primary" 
                  sx={{ mr: 1, transition: 'all 0.3s ease' }} 
                />
                <Typography 
                  className="card-title" 
                  variant="h6" 
                  fontSize="0.9rem" 
                  sx={{ transition: 'all 0.3s ease' }}
                >
                  {t('financial:renter.statistics.reservations', 'Réservations')}
                </Typography>
              </Box>
              <Typography 
                className="card-value" 
                variant="h4" 
                color="primary" 
                sx={{ transition: 'all 0.3s ease', mb: 1 }}
              >
                {statistics.uniqueReservationsCount || 0}
              </Typography>
              <Box>
                {/* Afficher les statuts de réservation (pas de paiement) */}
                {Object.entries(getFilteredBreakdowns().reservationsStats?.byStatus || {}).slice(0, 2).map(([status, count]) => (
                  <Typography key={status} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {status === 'confirmed' ? t('financial:renter.reservationStatus.confirmed', '✅ Confirmées') : 
                     status === 'pending' ? t('financial:renter.reservationStatus.pending', '⏳ En attente') : 
                     status === 'cancelled' ? t('financial:renter.reservationStatus.cancelled', '❌ Annulées') : 
                     status === 'completed' ? t('financial:renter.reservationStatus.completed', '🏁 Terminées') :
                     status === 'active' ? t('financial:renter.reservationStatus.active', '🟢 Actives') :
                     status === 'expired' ? t('financial:renter.reservationStatus.expired', '⏰ Expirées') :
                     status === 'draft' ? t('financial:renter.reservationStatus.draft', '📝 Brouillons') :
                     status === 'booked' ? t('financial:renter.reservationStatus.booked', '📋 Réservées') :                    
                     status === 'unknown' ? t('financial:renter.reservationStatus.unknown', '❓ Statut inconnu') :
                     `📋 ${status}`}: {count}
                  </Typography>
                ))}
                {Object.keys(getFilteredBreakdowns().reservationsStats?.byStatus || {}).length > 2 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    +{Object.keys(getFilteredBreakdowns().reservationsStats?.byStatus || {}).length - 2} {t('financial:renter.statistics.otherStatuses', 'autres statuts')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Transactions de paiement - basées sur l'historique */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              height: '180px', // Hauteur fixe
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[8],
                background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.light, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                '& .transaction-icon': {
                  color: theme.palette.secondary.main,
                  transform: 'scale(1.1)'
                },
                '& .transaction-title': {
                  color: theme.palette.secondary.main
                },
                '& .transaction-value': {
                  color: theme.palette.secondary.dark
                }
              }
            }}
            onClick={() => {
              const historySection = document.getElementById('payment-history-section');
              if (historySection) {
                historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHighlightHistory(true);
                setTimeout(() => setHighlightHistory(false), 2000);
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon 
                  className="transaction-icon" 
                  color="secondary" 
                  sx={{ mr: 1, transition: 'all 0.3s ease' }} 
                />
                <Typography 
                  className="transaction-title" 
                  variant="h6" 
                  fontSize="0.9rem"
                  sx={{ transition: 'all 0.3s ease' }}
                >
                  {t('financial:renter.statistics.transactions', 'Transactions')}
                </Typography>
              </Box>
              <Typography 
                className="transaction-value" 
                variant="h4" 
                color="secondary"
                sx={{ transition: 'all 0.3s ease', mb: 1 }}
              >
                {statistics.totalTransactions || 0}
              </Typography>
              <Box>
                {Object.entries(getFilteredBreakdowns().paymentsStats?.byStatus || {}).slice(0, 2).map(([status, count]) => (
                  <Typography key={status} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {status === 'completed' ? t('financial:renter.paymentStatus.completed', '✅ Complétées') : 
                     status === 'pending' ? t('financial:renter.paymentStatus.pending', '⏳ En attente') : 
                     status === 'failed' ? t('financial:renter.paymentStatus.failed', '❌ Échouées') : 
                     status === 'cancelled' ? t('financial:renter.paymentStatus.cancelled', '🚫 Annulées') : 
                     `📋 ${status}`}: {count}
                  </Typography>
                ))}
                {Object.keys(getFilteredBreakdowns().paymentsStats?.byStatus || {}).length > 2 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    +{Object.keys(getFilteredBreakdowns().paymentsStats?.byStatus || {}).length - 2} {t('financial:renter.statistics.otherStatuses', 'autres statuts')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Dépôts de garantie détaillés */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              height: '180px', // Hauteur fixe
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[8],
                background: `linear-gradient(45deg, ${alpha(theme.palette.warning.light, 0.1)}, ${alpha(theme.palette.warning.main, 0.1)})`,
                '& .deposit-icon': {
                  color: theme.palette.warning.main,
                  transform: 'scale(1.1)'
                },
                '& .deposit-title': {
                  color: theme.palette.warning.main
                },
                '& .deposit-value': {
                  color: theme.palette.warning.dark
                }
              }
            }}
            onClick={() => {
              const depositsSection = document.getElementById('deposits-section');
              if (depositsSection) {
                depositsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHighlightDeposits(true);
                setTimeout(() => setHighlightDeposits(false), 2000);
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance 
                  className="deposit-icon" 
                  color="warning" 
                  sx={{ mr: 1, transition: 'all 0.3s ease' }} 
                />
                <Typography 
                  className="deposit-title" 
                  variant="h6" 
                  fontSize="0.9rem" 
                  sx={{ transition: 'all 0.3s ease' }}
                >
                  {t('financial:renter.statistics.deposits', 'Dépôts')}
                </Typography>
              </Box>
              <Typography 
                className="deposit-value" 
                variant="h4" 
                color="warning.main"
                sx={{ transition: 'all 0.3s ease', mb: 1 }}
              >
                {getFilteredBreakdowns().depositsStats?.total || 0}
              </Typography>
              <Box>
                {Object.entries(getFilteredBreakdowns().depositsStats?.byStatus || {}).slice(0, 2).map(([status, count]) => (
                  <Typography key={status} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {status === 'held' ? t('financial:deposit.status.held', '🔒 Bloqués') : 
                     status === 'completed' ? t('financial:deposit.status.completed', '✅ Complétés') : 
                     status === 'pending' ? t('financial:deposit.status.pending', '⏳ En attente') : 
                     status === 'refunded' ? t('financial:deposit.status.refunded', '💸 Remboursés') : 
                     `📋 ${status}`}: {count}
                  </Typography>
                ))}
                {getFilteredBreakdowns().depositsStats?.cancelled > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('financial:deposit.status.cancelled', '❌ Annulés')}: {getFilteredBreakdowns().depositsStats.cancelled}
                  </Typography>
                )}
                {Object.keys(getFilteredBreakdowns().depositsStats?.byStatus || {}).length > 2 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    +{Object.keys(getFilteredBreakdowns().depositsStats?.byStatus || {}).length - 2} {t('financial:renter.statistics.otherStatuses', 'autres statuts')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Période d'Activité */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              height: '180px', // Hauteur fixe
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[8],
                background: `linear-gradient(45deg, ${alpha(theme.palette.info.light, 0.1)}, ${alpha(theme.palette.info.main, 0.1)})`,
                '& .period-icon': {
                  color: theme.palette.info.main,
                  transform: 'scale(1.1)'
                },
                '& .period-title': {
                  color: theme.palette.info.main
                },
                '& .period-value': {
                  color: theme.palette.info.dark
                }
              }
            }}
            onClick={() => {
              const historySection = document.getElementById('payment-history-section');
              if (historySection) {
                historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHighlightHistory(true);
                setTimeout(() => setHighlightHistory(false), 2000);
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ReceiptIcon 
                  className="period-icon" 
                  color="info" 
                  sx={{ mr: 1, transition: 'all 0.3s ease' }} 
                />
                <Typography 
                  className="period-title" 
                  variant="h6" 
                  fontSize="0.9rem"
                  sx={{ transition: 'all 0.3s ease' }}
                >
                  {t('financial:renter.statistics.activityPeriod', 'Période d\'Activité')}
                </Typography>
              </Box>
              <Typography 
                className="period-value" 
                variant="body2" 
                color="info.main" 
                sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1, transition: 'all 0.3s ease' }}
              >
                {statistics.firstPaymentDate ? 
                  `${t('financial:renter.statistics.since', 'Depuis le')} ${formatDate(statistics.firstPaymentDate)}` : 
                  t('financial:renter.statistics.noPayment', 'Aucun paiement')
                }
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('financial:renter.statistics.last', 'Dernier:')} {statistics.lastPaymentDate ? 
                  formatDate(statistics.lastPaymentDate) : 
                  'N/A'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Transaction la plus importante */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              height: '180px', // Hauteur fixe
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: theme.shadows[8],
                background: `linear-gradient(45deg, ${alpha(theme.palette.success.light, 0.1)}, ${alpha(theme.palette.success.main, 0.1)})`,
                '& .largest-icon': {
                  color: theme.palette.success.main,
                  transform: 'scale(1.1)'
                },
                '& .largest-title': {
                  color: theme.palette.success.main
                },
                '& .largest-value': {
                  color: theme.palette.success.dark
                }
              }
            }}
            onClick={() => {
              const historySection = document.getElementById('payment-history-section');
              if (historySection) {
                historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHighlightHistory(true);
                setTimeout(() => setHighlightHistory(false), 2000);
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EarningsIcon 
                  className="largest-icon" 
                  color="success" 
                  sx={{ mr: 1, transition: 'all 0.3s ease' }} 
                />
                <Typography 
                  className="largest-title" 
                  variant="h6" 
                  fontSize="0.9rem"
                  sx={{ transition: 'all 0.3s ease' }}
                >
                  {t('financial:renter.statistics.largestTransaction', 'Plus Grosse Transaction')}
                </Typography>
              </Box>
              <Typography 
                className="largest-value" 
                variant="h5" 
                color="success.main" 
                fontSize="1.5rem"
                sx={{ mb: 1, transition: 'all 0.3s ease' }}
              >
                {statistics.transactionStats?.largestTransaction ? 
                  formatCurrency(statistics.transactionStats.largestTransaction.amount, statistics.transactionStats.largestTransaction.currency) : 
                  formatCurrency(0)
                }
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {statistics.transactionStats?.largestTransaction?.date ? 
                  `${t('financial:renter.statistics.on', 'Le')} ${formatDate(statistics.transactionStats.largestTransaction.date)}` : 
                  t('financial:renter.statistics.noTransaction', 'Aucune transaction')
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Répartition CASH vs ONLINE */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              p: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderLeft: `4px solid ${theme.palette.primary.main}`
            }}
          >
            <CardIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            {t('financial:renter.paymentMethods.title', 'Méthodes de Paiement')}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Cash */}
            <Grid item xs={12} sm={6}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, #ffffff, #fff8e1)',
                  boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(255, 152, 0, 0.25)',
                    transform: 'translateY(-3px)'
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#e65100',
                    fontWeight: 600
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      mr: 1.5, 
                      p: 1, 
                      borderRadius: '50%', 
                      bgcolor: alpha('#ff9800', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    💰
                  </Box>
                  {t('financial:renter.paymentMethods.cash', 'Paiements Cash')}
                </Typography>
                {/* Répartition par devise pour Cash */}
                <Box sx={{ mt: 3 }}>
                  {Object.entries(getFilteredBreakdowns().cashFiltered.byCurrency || {})
                    .slice(0, expandedCurrency.cash)
                    .map(([currency, currData]) => (
                      <Box 
                        key={currency} 
                        sx={{ 
                          mb: 2,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: alpha('#ff9800', 0.07),
                          border: '1px solid',
                          borderColor: alpha('#ff9800', 0.2),
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#424242' }}>
                          {currency}
                        </Typography>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100', textAlign: 'right' }}>
                            {formatCurrency(currData.amount, currency)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#757575', display: 'block', textAlign: 'right' }}>
                            {currData.count} {currData.count === 1 ? t('financial:renter.currencyBreakdown.transactions', 'transaction') : t('financial:renter.currencyBreakdown.transactionsPlural', 'transactions')}
                          </Typography>
                          {/* Détail breakdown */}
                          {currData.breakdown && (
                            <Box sx={{ mt: 0.5, textAlign: 'right' }}>
                              {currData.breakdown.rental > 0 && (
                                <Typography variant="caption" sx={{ color: '#4caf50', display: 'block', fontSize: '0.7rem' }}>
                                  {t('financial:breakdownLabels.rental', 'Location')}: {formatCurrency(currData.breakdown.rental, currency)}
                                </Typography>
                              )}
                              {currData.breakdown.commission > 0 && (
                                <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', fontSize: '0.7rem' }}>
                                  {t('financial:breakdownLabels.commission', 'Commission')}: {formatCurrency(currData.breakdown.commission, currency)}
                                </Typography>
                              )}
                              {currData.breakdown.tax > 0 && (
                                <Typography variant="caption" sx={{ color: '#f44336', display: 'block', fontSize: '0.7rem' }}>
                                  {t('financial:breakdownLabels.tax', 'Taxes')}: {formatCurrency(currData.breakdown.tax, currency)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))
                  }
                  
                  {/* Bouton "Voir plus" si d'autres devises existent */}
                  {Object.keys(getFilteredBreakdowns().cashFiltered.byCurrency || {}).length > expandedCurrency.cash && (
                    <Button
                      variant="contained"
                      color="warning"
                      fullWidth
                      onClick={() => handleShowMoreCurrencies('cash')}
                      sx={{ 
                        mt: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderTop: `1px dashed ${alpha('#ff9800', 0.3)}`,
                        borderRadius: 2,
                        py: 1,
                        boxShadow: '0 4px 10px rgba(255, 152, 0, 0.15)',
                        '&:hover': {
                          boxShadow: '0 6px 15px rgba(255, 152, 0, 0.2)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('financial:renter.paymentMethods.showAllCurrencies', 'Afficher toutes les devises')} ({Object.keys(getFilteredBreakdowns().cashFiltered.byCurrency || {}).length - expandedCurrency.cash} {(Object.keys(getFilteredBreakdowns().cashFiltered.byCurrency || {}).length - expandedCurrency.cash) > 1 ? t('financial:renter.statistics.restainings', 'restantes') : t('financial:renter.statistics.restaining', 'restante')})
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Online */}
            <Grid item xs={12} sm={6}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, #ffffff, #e8f5fe)',
                  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(25, 118, 210, 0.25)',
                    transform: 'translateY(-3px)'
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#0d47a1',
                    fontWeight: 600
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      mr: 1.5, 
                      p: 1, 
                      borderRadius: '50%', 
                      bgcolor: alpha('#1976d2', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    💳
                  </Box>
                  {t('financial:renter.paymentMethods.online', 'Paiements En Ligne')}
                </Typography>
                {/* Répartition par devise pour Online */}
                <Box sx={{ mt: 3 }}>
                  {Object.entries(getFilteredBreakdowns().onlineFiltered.byCurrency || {})
                    .slice(0, expandedCurrency.online)
                    .map(([currency, currData]) => (
                      <Box 
                        key={currency} 
                        sx={{ 
                          mb: 2,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: alpha('#1976d2', 0.07),
                          border: '1px solid',
                          borderColor: alpha('#1976d2', 0.2),
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#424242' }}>
                          {currency}
                        </Typography>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d47a1', textAlign: 'right' }}>
                            {formatCurrency(currData.amount, currency)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#757575', display: 'block', textAlign: 'right' }}>
                            {currData.count} {currData.count === 1 ? t('financial:renter.currencyBreakdown.transactions', 'transaction') : t('financial:renter.currencyBreakdown.transactionsPlural', 'transactions')}
                          </Typography>
                          {/* Détail breakdown */}
                          {currData.breakdown && (
                            <Box sx={{ mt: 0.5, textAlign: 'right' }}>
                              {currData.breakdown.rental > 0 && (
                                <Typography variant="caption" sx={{ color: '#4caf50', display: 'block', fontSize: '0.7rem' }}>
                                  {t('financial:breakdownLabels.rental', 'Location')}: {formatCurrency(currData.breakdown.rental, currency)}
                                </Typography>
                              )}
                              {currData.breakdown.commission > 0 && (
                                <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', fontSize: '0.7rem' }}>
                                  {t('financial:breakdownLabels.commission', 'Commission')}: {formatCurrency(currData.breakdown.commission, currency)}
                                </Typography>
                              )}
                              {currData.breakdown.tax > 0 && (
                                <Typography variant="caption" sx={{ color: '#f44336', display: 'block', fontSize: '0.7rem' }}>
                                  {t('financial:breakdownLabels.tax', 'Taxes')}: {formatCurrency(currData.breakdown.tax, currency)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))
                  }
                  
                  {/* Bouton "Voir plus" si d'autres devises existent */}
                  {Object.keys(getFilteredBreakdowns().onlineFiltered.byCurrency || {}).length > expandedCurrency.online && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => handleShowMoreCurrencies('online')}
                      sx={{ 
                        mt: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderTop: `1px dashed ${alpha('#1976d2', 0.3)}`,
                        borderRadius: 2,
                        py: 1,
                        boxShadow: '0 4px 10px rgba(25, 118, 210, 0.15)',
                        '&:hover': {
                          boxShadow: '0 6px 15px rgba(25, 118, 210, 0.2)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('financial:renter.paymentMethods.showAllCurrencies', 'Afficher toutes les devises')} ({Object.keys(getFilteredBreakdowns().onlineFiltered.byCurrency || {}).length - expandedCurrency.online} {(Object.keys(getFilteredBreakdowns().onlineFiltered.byCurrency || {}).length - expandedCurrency.online) > 1 ? t('financial:renter.statistics.restainings', 'restantes') : t('financial:renter.statistics.restaining', 'restante')})
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dépôts de Garantie Détaillés */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              p: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.warning.main, 0.05),
              borderLeft: `4px solid ${theme.palette.warning.main}`
            }}
          >
            <AccountBalance sx={{ mr: 1, color: theme.palette.warning.main }} />
            {t('financial:deposit.detailed', 'Dépôts de Garantie Détaillés')}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Total des dépôts */}
            <Grid item xs={12} sm={6}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, #ffffff, #fff3e0)',
                  boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(255, 152, 0, 0.25)',
                    transform: 'translateY(-3px)'
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#e65100',
                    fontWeight: 600
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      mr: 1.5, 
                      p: 1, 
                      borderRadius: '50%', 
                      bgcolor: alpha('#ff9800', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    🏦
                  </Box>
                  {t('financial:deposit.overview', 'Vue d\'Ensemble')}
                </Typography>
                <Typography variant="h3" sx={{ mb: 1, color: '#e65100', fontWeight: 700 }}>
                  {getFilteredBreakdowns().depositsStats?.total || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('financial:deposit.total', 'Dépôts au total')}
                </Typography>
                
                {/* Répartition par statut */}
                <Box sx={{ mt: 2 }}>
                  {Object.entries(getFilteredBreakdowns().depositsStats?.byStatus || {}).map(([status, count]) => (
                    <Box 
                      key={status} 
                      sx={{ 
                        mb: 1,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: status === 'held' ? alpha('#ff9800', 0.1) : 
                                status === 'completed' ? alpha('#4caf50', 0.1) : 
                                status === 'pending' ? alpha('#2196f3', 0.1) : 
                                alpha('#757575', 0.1),
                        border: '1px solid',
                        borderColor: status === 'held' ? alpha('#ff9800', 0.3) : 
                                   status === 'completed' ? alpha('#4caf50', 0.3) : 
                                   status === 'pending' ? alpha('#2196f3', 0.3) : 
                                   alpha('#757575', 0.3),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: status === 'held' ? '#e65100' : 
                                 status === 'completed' ? '#2e7d32' : 
                                 status === 'pending' ? '#1565c0' : '#424242'
                        }}
                      >
                        {status === 'held' ? t('financial:deposit.status.held', '🔒 Bloqués') : 
                         status === 'completed' ? t('financial:deposit.status.completed', '✅ Complétés') : 
                         status === 'pending' ? t('financial:deposit.status.pending', '⏳ En attente') : 
                         status === 'refunded' ? t('financial:deposit.status.refunded', '💸 Remboursés') : 
                         status === 'cancelled' ? t('financial:deposit.status.cancelled', '❌ Annulés') :
                         status === 'paid' ? t('financial:deposit.status.paid', '✅ Payés') :
                         status === 'released' ? t('financial:deposit.status.released', '✅ Libérés') :
                         status === 'partially_released' ? t('financial:deposit.status.partially_released', '⚠️ Partiellement libérés') :
                         status === 'partially_captured' ? t('financial:deposit.status.partially_captured', '⚠️ Partiellement capturés') :
                         status === 'withheld' ? t('financial:deposit.status.withheld', '🚫 Retenus') :
                         status === 'disputed' ? t('financial:deposit.status.disputed', '⚖️ Contestés') :
                         `❌ ${status}`}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          color: status === 'held' ? '#e65100' : 
                                 status === 'completed' ? '#2e7d32' : 
                                 status === 'pending' ? '#1565c0' : '#424242'
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  ))}
                  
                  {/* Dépôts annulés */}
                  {getFilteredBreakdowns().depositsStats?.cancelled > 0 && (
                    <Box 
                      sx={{ 
                        mb: 1,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha('#f44336', 0.1),
                        border: '1px solid',
                        borderColor: alpha('#f44336', 0.3),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#c62828' }}>
                        {t('financial:deposit.status.cancelled', '❌ Annulés')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828' }}>
                        {getFilteredBreakdowns().depositsStats.cancelled}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Répartition par devise */}
            <Grid item xs={12} sm={6}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, #ffffff, #e8f5fe)',
                  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(25, 118, 210, 0.25)',
                    transform: 'translateY(-3px)'
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#0d47a1',
                    fontWeight: 600
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      mr: 1.5, 
                      p: 1, 
                      borderRadius: '50%', 
                      bgcolor: alpha('#1976d2', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    💱
                  </Box>
                  {t('financial:deposit.current', 'En cours')}
                </Typography>
                
                {/* Répartition par devise */}
                <Box sx={{ mt: 2 }}>
                  {Object.entries(getFilteredBreakdowns().depositsFiltered).map(([currency, data]) => (
                    <Box 
                      key={currency} 
                      sx={{ 
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha('#1976d2', 0.07),
                        border: '1px solid',
                        borderColor: alpha('#1976d2', 0.2),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#424242' }}>
                          {currency}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          {data.count} {data.count === 1 ? t('financial:deposit.title', 'dépôt').toLowerCase() : t('financial:deposit.title', 'dépôts').toLowerCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d47a1' }}>
                          {formatCurrency(data.totalAmount, currency)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          {t('financial:renter.statistics.average', 'Moy:')} {formatCurrency(data.totalAmount / data.count, currency)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  
                  {Object.keys(getFilteredBreakdowns().depositsFiltered).length === 0 && (
                    <Box sx={{ 
                      p: 2, 
                      textAlign: 'center', 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}>
                      {t('financial:deposit.noActiveDeposit', 'Aucun dépôt actif')}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Répartition par DEVISE */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              p: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderLeft: `4px solid ${theme.palette.primary.main}`
            }}
          >
            <SwapIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            {t('financial:renter.currencyBreakdown.title', 'Répartition par Devise')}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(getFilteredBreakdowns().currencyBreakdownFiltered || {})
              .filter(([_, data]) => data.transactions > 0)
              .slice(0, expandedCurrency.general)
              .map(([currency, data]) => (
                <Grid item xs={12} sm={6} md={4} key={currency}>
                  <Paper 
                    elevation={2}
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 2,
                      background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box 
                        component="span" 
                        sx={{ 
                          mr: 1.5, 
                          p: 0.8,
                          borderRadius: '50%', 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32
                        }}
                      >
                        
                      </Box>
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        {currency}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: theme.palette.text.primary, fontWeight: 700 }}>
                      {formatCurrency(data.totalAmount, currency)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {data.transactions} {data.transactions === 1 ? t('financial:renter.currencyBreakdown.transactions', 'transaction') : t('financial:renter.currencyBreakdown.transactionsPlural', 'transactions')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('financial:renter.currencyBreakdown.average', 'Moyenne:')} {formatCurrency(data.avgTransaction, currency)}
                    </Typography>
                    
                    {/* Détails Rental + Commission + Tax */}
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                        {t('financial:renter.paymentMethods.details', 'Détails:')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                        {t('financial:breakdownLabels.rental', 'Location')}: {formatCurrency(data.breakdown?.rental || 0, currency)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {t('financial:breakdownLabels.commission', 'Commission')}: {formatCurrency(data.breakdown?.commission || 0, currency)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {t('financial:breakdownLabels.tax', 'Taxes')}: {formatCurrency(data.breakdown?.tax || 0, currency)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', color: '#e65100', fontWeight: 500 }}>
                          Cash
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                          {formatCurrency(data.byMethod?.cash || 0, currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: '#0d47a1', fontWeight: 500 }}>
                          En ligne
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0d47a1' }}>
                          {formatCurrency(data.byMethod?.online || 0, currency)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {data.firstDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center', fontStyle: 'italic' }}>
                        {formatDate(data.firstDate)} - {formatDate(data.lastDate)}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))
            }
          </Grid>
          
          {/* Bouton "Voir plus de devises" */}
          {Object.entries(getFilteredBreakdowns().currencyBreakdownFiltered || {}).filter(([_, data]) => data.transactions > 0).length > expandedCurrency.general && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => handleShowMoreCurrencies('general')}
              sx={{ 
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
                  transform: 'translateY(-2px)'
                }
              }}
              startIcon={<AddIcon />}
            >
              {t('financial:renter.paymentMethods.showAllCurrencies', 'Afficher toutes les devises')} ({Object.entries(getFilteredBreakdowns().currencyBreakdownFiltered || {}).filter(([_, data]) => data.transactions > 0).length - expandedCurrency.general} {(Object.entries(getFilteredBreakdowns().currencyBreakdownFiltered || {}).filter(([_, data]) => data.transactions > 0).length - expandedCurrency.general) > 1 ? t('financial:renter.statistics.restainings', 'restantes') : t('financial:renter.statistics.restaining', 'restante')})
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Alertes importantes */}
      {statistics.pendingPayments > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('financial:renter.alerts.pendingPayments', 
            'Vous avez {{count}} paiement(s) en attente. Veuillez vérifier vos moyens de paiement.',
            { count: statistics.pendingPayments }
          )}
        </Alert>
      )}

      {/* Historique des paiements avec pagination infinie */}
      <Box id="payment-history-section" sx={{ scrollMarginTop: '80px', transition: 'all 0.5s ease', mb: 4 }}>
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.5s ease',
            ...(highlightHistory && {
              boxShadow: `0 0 20px ${theme.palette.primary.main}`,
              transform: 'scale(1.01)',
            })
          }}
        >
          {highlightHistory && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                animation: `${pulseAnimation} 1.5s infinite`
              }}
            />
          )}
          <CardContent>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, #42a5f5 30%, #1976d2 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              <ReceiptIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('financial:renter.historyTitle', 'Historique des Paiements')}
              </Typography>
            </Box>
            <PaymentHistoryInfinite 
              filters={{}}
              onError={(error) => {
                console.error('Error in PaymentHistoryInfinite:', error);
                toast.error(t('financial:errors.paymentHistoryError', 'Erreur lors du chargement de l\'historique des paiements'));
              }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Section des dépôts avec pagination infinie */}
      <Box id="deposits-section" sx={{ scrollMarginTop: '80px', transition: 'all 0.5s ease' }}>
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.5s ease',
            ...(highlightDeposits && {
              boxShadow: `0 0 20px ${theme.palette.warning.main}`,
              transform: 'scale(1.01)',
            })
          }}
        >
          {highlightDeposits && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                animation: `${pulseAnimation} 1.5s infinite`
              }}
            />
          )}
          <CardContent>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, #ff9800 30%, #f57c00 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              <SecurityIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('financial:deposit.historyTitle', 'Dépôts de Garantie')}
              </Typography>
            </Box>
            <DepositsHistoryInfinite 
              filters={{}}
              onError={(error) => {
                console.error('Error in DepositsHistoryInfinite:', error);
                toast.error(t('financial:errors.depositsError', 'Erreur lors du chargement des dépôts de garantie'));
              }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Drawer Sidebar */}
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={toggleSidebar}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', md: 350 },
            maxWidth: '100vw',
            height: '100%',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <SidebarContent />
      </Drawer>

      {/* FAB flottant pour ouvrir le sidebar */}
      {!sidebarOpen && (
        <Tooltip title={t('financial:sidebar.open', 'Ouvrir le menu financier')} placement="left">
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              top: '50%',
              right: 24,
              transform: 'translateY(-50%)',
              zIndex: 1301,
              animation: `${pulseAnimation} 2s ease-in-out infinite`,
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              '&:hover': {
                animation: 'none',
                transform: 'translateY(-50%) scale(1.15)',
                boxShadow: '0 12px 25px rgba(98, 0, 238, 0.6)',
                transition: 'all 0.3s ease',
                background: 'linear-gradient(45deg, #6200ee 40%, #03dac4 80%)'
              },
              '&:active': {
                transform: 'translateY(-50%) scale(0.95)',
                transition: 'all 0.1s ease'
              }
            }}
            onClick={toggleSidebar}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default RenterFinancialCenter;
